import './WritingStation.scss';
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar';
import Editor from '../../components/Editor/Editor';
import BookSettings from '../../components/BookSettings/BookSettings';
import PublishDialog from '../../components/PublishDialog/PublishDialog';
import VersionHistory from '../../components/VersionHistory/VersionHistory';
import AuthContext from '../../context/AuthContext';
import SnackbarContext from '../../context/SnackbarContext';
import { API_BASE_URL } from '../../config';
import { authHeaders } from '../../utils/api';

const EMPTY_DOC = { type: "doc", content: [{ type: "paragraph" }] };

function parseContent(raw) {
    if (!raw) return EMPTY_DOC;
    if (typeof raw === "object") return raw;
    try { return JSON.parse(raw); } catch { return EMPTY_DOC; }
}

function makeChapter(order, title = "") {
    return {
        clientId: Math.random().toString(36).slice(2),
        id: null,
        order,
        title,
        content: EMPTY_DOC,
    };
}

const CATEGORY_LABELS = {
    novel: 'رواية', short: 'قصة قصيرة', poetry: 'شعر', play: 'مسرحية',
    essay: 'مقالات', memoir: 'سيرة ذاتية', scifi: 'خيال علمي', mystery: 'غموض',
    romance: 'رومانسية', historic: 'تاريخي', children: 'أطفال', other: 'أخرى',
};

function formatRelative(date) {
    if (!date) return '';
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 5) return 'الآن';
    if (seconds < 60) return `قبل ${seconds} ث`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `قبل ${minutes} د`;
    return date.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });
}

function SaveStatus({ status, lastSavedAt }) {
    const [, setTick] = React.useState(0);
    React.useEffect(() => {
        if (status !== 'saved') return;
        const id = setInterval(() => setTick(t => t + 1), 15000);
        return () => clearInterval(id);
    }, [status]);

    const variants = {
        saved:  { label: lastSavedAt ? `محفوظ · ${formatRelative(lastSavedAt)}` : 'محفوظ', icon: '✓' },
        saving: { label: 'جارٍ الحفظ التلقائي…', icon: '⟳' },
        dirty:  { label: 'تغييرات غير محفوظة', icon: '•' },
        error:  { label: 'تعذر الحفظ التلقائي', icon: '!' },
        idle:   { label: '', icon: '' },
    };
    const v = variants[status] || variants.idle;
    if (!v.label) return null;
    return (
        <span className={`ws-savestatus ws-savestatus--${status}`} role='status' aria-live='polite'>
            <span className='ws-savestatus-icon' aria-hidden='true'>{v.icon}</span>
            <span>{v.label}</span>
        </span>
    );
}

export default function WritingStation() {

    const navigate = useNavigate();
    const { user, authTokens } = useContext(AuthContext);
    const { setSnackbar } = useContext(SnackbarContext);
    const [saving, setSaving] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [publishOpen, setPublishOpen] = useState(false);
    const [versionsOpen, setVersionsOpen] = useState(false);

    // Auto-save: 'saved' | 'dirty' | 'saving' | 'error' | 'idle'
    // 'idle' = nothing to save yet (new book, no title).
    const [saveStatus, setSaveStatus] = useState('idle');
    const [lastSavedAt, setLastSavedAt] = useState(null);

    const location = useLocation();
    const [bookId, setBookId] = useState(location.state?.book_id ?? null);
    const hasLoadedRef = useRef(location.state?.book_id == null);
    const autoSaveTimerRef = useRef(null);
    const persistRef = useRef(null);

    const [title, setTitle] = useState('');
    const [isPublished, setIsPublished] = useState(false);
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('');
    const [penName, setPenName] = useState('');
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [coverImage, setCoverImage] = useState(null);

    const [chapters, setChapters] = useState([makeChapter(0, 'الفصل الأول')]);
    const [activeIdx, setActiveIdx] = useState(0);

    useEffect(() => {
        if (bookId == null) return;

        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(`${API_BASE_URL}/api/books/get-book/?id=${bookId}`, {
                    headers: authHeaders(authTokens),
                });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data = await r.json();
                const fetched = data.book?.[0];
                if (cancelled || !fetched) return;

                setTitle(fetched.title ?? '');
                setIsPublished(!!fetched.is_published);
                setDescription(fetched.description ?? '');
                setCategory(fetched.category ?? '');
                setPenName(fetched.pen_name ?? '');
                setIsAnonymous(!!fetched.is_anonymous);
                setCoverImage(fetched.cover_image ?? null);

                const remoteChapters = (fetched.chapters ?? []).map((ch, i) => ({
                    clientId: Math.random().toString(36).slice(2),
                    id: ch.id,
                    order: ch.order ?? i,
                    title: ch.title ?? '',
                    content: parseContent(ch.content),
                }));

                if (remoteChapters.length === 0) {
                    setChapters([makeChapter(0, 'الفصل الأول')]);
                } else {
                    setChapters(remoteChapters);
                }
                setActiveIdx(0);
                setSaveStatus('saved');
                setLastSavedAt(new Date());
                // Enable autosave after the initial fetch settles, on the next tick,
                // so the state setters above don't immediately mark the doc dirty.
                setTimeout(() => { hasLoadedRef.current = true; }, 0);
            } catch (err) {
                setSnackbar({ content: 'تعذر تحميل الكتاب', success: false });
            }
        })();
        return () => { cancelled = true; };
    }, [bookId, authTokens, setSnackbar]);

    const updateChapter = useCallback((idx, patch) => {
        setChapters(prev => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
    }, []);

    const addChapter = () => {
        setChapters(prev => {
            const next = [...prev, makeChapter(prev.length, `الفصل ${prev.length + 1}`)];
            setActiveIdx(next.length - 1);
            return next;
        });
        setSidebarOpen(false);
    };

    const removeChapter = (idx) => {
        if (chapters.length <= 1) {
            setSnackbar({ content: 'يجب أن يحتوي الكتاب على فصل واحد على الأقل', success: false });
            return;
        }
        setChapters(prev => prev
            .filter((_, i) => i !== idx)
            .map((c, i) => ({ ...c, order: i }))
        );
        setActiveIdx(i => Math.max(0, i >= idx ? i - 1 : i));
    };

    const moveChapter = (idx, delta) => {
        setChapters(prev => {
            const target = idx + delta;
            if (target < 0 || target >= prev.length) return prev;
            const next = [...prev];
            [next[idx], next[target]] = [next[target], next[idx]];
            return next.map((c, i) => ({ ...c, order: i }));
        });
        if (activeIdx === idx) setActiveIdx(idx + delta);
        else if (activeIdx === idx + delta) setActiveIdx(idx);
    };

    // Auto-save: 1.5s after the last edit. Skipped if there's no title yet.
    useEffect(() => {
        if (!hasLoadedRef.current) return;

        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        if (!title.trim()) { setSaveStatus('idle'); return; }

        setSaveStatus('dirty');
        autoSaveTimerRef.current = setTimeout(() => {
            persistRef.current?.({ silent: true });
        }, 1500);

        return () => {
            if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        };
    }, [title, description, category, penName, isAnonymous, chapters]);

    // Warn on close/refresh if there are unsaved changes.
    useEffect(() => {
        const isDirty = saveStatus === 'dirty' || saveStatus === 'saving' || saveStatus === 'error';
        if (!isDirty) return;
        const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [saveStatus]);

    const snapshotAuto = useCallback(async () => {
        if (!bookId) return;
        try {
            await fetch(`${API_BASE_URL}/api/books/versions/auto/`, {
                method: 'POST',
                headers: { ...authHeaders(authTokens), 'Content-Type': 'application/json' },
                body: JSON.stringify({ book: bookId }),
            });
            // Server returns {skipped: true} if within cooldown — no action needed.
        } catch { /* silent — versioning is best-effort */ }
    }, [bookId, authTokens]);

    const snapshotManual = useCallback(async (label = '') => {
        if (!bookId) return;
        try {
            await fetch(`${API_BASE_URL}/api/books/versions/`, {
                method: 'POST',
                headers: { ...authHeaders(authTokens), 'Content-Type': 'application/json' },
                body: JSON.stringify({ book: bookId, label }),
            });
        } catch { /* silent */ }
    }, [bookId, authTokens]);

    // Auto-snapshot every 10 min while the page is open and the book has an ID.
    // Backend enforces the cooldown — frontend is just a trigger.
    useEffect(() => {
        if (!bookId) return;
        const interval = setInterval(() => { snapshotAuto(); }, 10 * 60 * 1000);
        return () => clearInterval(interval);
    }, [bookId, snapshotAuto]);

    const persist = async ({ publishOverride, silent = false } = {}) => {
        if (!title.trim()) {
            if (!silent) {
                setSnackbar({ content: 'الرجاء إدخال عنوان الكتاب', success: false });
            }
            return null;
        }

        if (silent) setSaveStatus('saving');
        else setSaving(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/books/save-book/`, {
                method: 'POST',
                headers: { ...authHeaders(authTokens), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: bookId,
                    title,
                    description,
                    category,
                    pen_name: penName,
                    is_anonymous: isAnonymous,
                    is_published: publishOverride !== undefined ? publishOverride : isPublished,
                    chapters: chapters.map((c, i) => ({
                        order: i,
                        title: c.title,
                        content: JSON.stringify(c.content),
                    })),
                }),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const saved = await r.json();
            setBookId(saved.id);
            setCoverImage(saved.cover_image ?? null);
            setIsPublished(!!saved.is_published);
            setSaveStatus('saved');
            setLastSavedAt(new Date());
            return saved;
        } catch (err) {
            if (silent) setSaveStatus('error');
            else setSnackbar({ content: 'تعذر حفظ الكتاب', success: false });
            return null;
        } finally {
            if (!silent) setSaving(false);
        }
    };

    persistRef.current = persist;

    const onSaveDraft = async () => {
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        const saved = await persist();
        if (saved) {
            snapshotManual('حفظ يدوي');
            setSnackbar({ content: 'تم الحفظ', success: true });
            navigate('/myworks');
        }
    };

    const onConfirmPublish = async () => {
        const saved = await persist({ publishOverride: true });
        if (saved) {
            snapshotManual('نشر');
            setSnackbar({ content: 'تم النشر في المكتبة', success: true });
            setPublishOpen(false);
            navigate('/myworks');
        }
    };

    const onUnpublish = async () => {
        const saved = await persist({ publishOverride: false });
        if (saved) {
            setSnackbar({ content: 'تم إلغاء النشر', success: true });
            setPublishOpen(false);
        }
    };

    const onVersionRestored = async () => {
        // Refetch the book — restored chapters are now on the server.
        if (!bookId) return;
        try {
            const r = await fetch(`${API_BASE_URL}/api/books/get-book/?id=${bookId}`, {
                headers: authHeaders(authTokens),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            const fetched = data.book?.[0];
            if (!fetched) return;
            hasLoadedRef.current = false; // suppress dirty signal during state-replacement
            setTitle(fetched.title ?? '');
            setIsPublished(!!fetched.is_published);
            setDescription(fetched.description ?? '');
            setCategory(fetched.category ?? '');
            setPenName(fetched.pen_name ?? '');
            setIsAnonymous(!!fetched.is_anonymous);
            setCoverImage(fetched.cover_image ?? null);
            const remoteChapters = (fetched.chapters ?? []).map((ch, i) => ({
                clientId: Math.random().toString(36).slice(2),
                id: ch.id,
                order: ch.order ?? i,
                title: ch.title ?? '',
                content: parseContent(ch.content),
            }));
            setChapters(remoteChapters.length ? remoteChapters : [makeChapter(0, 'الفصل الأول')]);
            setActiveIdx(0);
            setSaveStatus('saved');
            setLastSavedAt(new Date());
            setTimeout(() => { hasLoadedRef.current = true; }, 0);
            setSnackbar({ content: 'تمت الاستعادة', success: true });
        } catch {
            setSnackbar({ content: 'تعذر إعادة تحميل الكتاب بعد الاستعادة', success: false });
        }
    };

    const activeChapter = chapters[activeIdx];
    const displayAuthor = isAnonymous ? 'مجهول' : (penName || user?.username || '');

    return (
        <>
        <Navbar />
        <div className='ws-page'>
            <aside className={'ws-sidebar' + (sidebarOpen ? ' is-open' : '')}>
                <div className='ws-sidebar-header'>
                    <h2>الفصول</h2>
                    <button className='ws-sidebar-close' onClick={() => setSidebarOpen(false)} aria-label='إغلاق'>×</button>
                </div>
                <ol className='ws-chapter-list'>
                    {chapters.map((c, i) => (
                        <li key={c.clientId} className={'ws-chapter' + (i === activeIdx ? ' is-active' : '')}>
                            <button
                                className='ws-chapter-select'
                                onClick={() => { setActiveIdx(i); setSidebarOpen(false); }}
                            >
                                <span className='ws-chapter-num'>{i + 1}</span>
                                <span className='ws-chapter-title'>{c.title || 'بدون عنوان'}</span>
                            </button>
                            <div className='ws-chapter-actions'>
                                <button onClick={() => moveChapter(i, -1)} disabled={i === 0} aria-label='تحريك للأعلى' title='للأعلى'>▲</button>
                                <button onClick={() => moveChapter(i, +1)} disabled={i === chapters.length - 1} aria-label='تحريك للأسفل' title='للأسفل'>▼</button>
                                <button onClick={() => removeChapter(i)} aria-label='حذف' title='حذف' className='ws-chapter-delete'>×</button>
                            </div>
                        </li>
                    ))}
                </ol>
                <button className='ws-add-chapter' onClick={addChapter}>+ فصل جديد</button>
            </aside>

            <main className='ws-main'>
                <div className='ws-topbar'>
                    <button className='ws-burger' onClick={() => setSidebarOpen(o => !o)} aria-label='الفصول'>☰</button>
                    <input
                        className='ws-book-title'
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder='عنوان الكتاب'
                        aria-label='عنوان الكتاب'
                    />
                    <SaveStatus status={saveStatus} lastSavedAt={lastSavedAt} />
                    <button
                        type='button'
                        className='ws-settings-button'
                        onClick={() => setSettingsOpen(true)}
                        aria-label='إعدادات الكتاب'
                        title='الغلاف، النبذة، التصنيف'
                    >
                        ⚙ التفاصيل
                    </button>
                    <button
                        type='button'
                        className='ws-settings-button'
                        onClick={() => setVersionsOpen(true)}
                        disabled={!bookId}
                        title={bookId ? 'سجل الإصدارات' : 'احفظ الكتاب أوّلاً'}
                        aria-label='سجل الإصدارات'
                    >
                        🕘 الإصدارات
                    </button>
                    <button type='button' className='ws-save-button ws-save-button--ghost' onClick={onSaveDraft} disabled={saving}>
                        {saving ? 'جارٍ الحفظ…' : 'حفظ'}
                    </button>
                    <button
                        type='button'
                        className={'ws-publish-button' + (isPublished ? ' is-published' : '')}
                        onClick={() => setPublishOpen(true)}
                        disabled={saving}
                    >
                        {isPublished ? 'محرّر منشور' : 'نشر'}
                    </button>
                </div>

                {activeChapter && (
                    <div className='ws-chapter-edit'>
                        <input
                            className='ws-chapter-title-input'
                            value={activeChapter.title}
                            onChange={(e) => updateChapter(activeIdx, { title: e.target.value })}
                            placeholder='عنوان الفصل'
                            aria-label='عنوان الفصل'
                        />
                        <Editor
                            key={activeChapter.clientId}
                            value={activeChapter.content}
                            onChange={(json) => updateChapter(activeIdx, { content: json })}
                        />
                    </div>
                )}
            </main>

            {sidebarOpen && <div className='ws-overlay' onClick={() => setSidebarOpen(false)} />}
        </div>

        <BookSettings
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            bookId={bookId}
            authTokens={authTokens}
            settings={{ description, category, penName, isAnonymous, coverImage }}
            onChange={(patch) => {
                if (patch.description !== undefined) setDescription(patch.description);
                if (patch.category !== undefined) setCategory(patch.category);
                if (patch.penName !== undefined) setPenName(patch.penName);
                if (patch.isAnonymous !== undefined) setIsAnonymous(patch.isAnonymous);
            }}
            onCoverChange={(url) => setCoverImage(url)}
            onUploadError={(msg) => setSnackbar({ content: msg, success: false })}
        />

        <PublishDialog
            open={publishOpen}
            onClose={() => setPublishOpen(false)}
            onConfirm={onConfirmPublish}
            onUnpublish={onUnpublish}
            submitting={saving}
            isAlreadyPublished={isPublished}
            book={{
                title,
                description,
                coverImage,
                categoryLabel: CATEGORY_LABELS[category] || '',
                isAnonymous,
                penName,
                displayAuthor,
                author: user?.username || '',
            }}
            chapters={chapters}
        />

        <VersionHistory
            open={versionsOpen}
            onClose={() => setVersionsOpen(false)}
            bookId={bookId}
            authTokens={authTokens}
            onRestored={onVersionRestored}
        />
        </>
    );
}
